import { useState } from "react";
import CMSTeaser from "../CMSTeaser/CMSTeaser";
import { type CMSTeaserProps } from "../../model";
import { NARROW, WIDE } from "../../../utils/constants";
import { CMSSection, type CMSSectionComponentProps } from "../CMSSection";
import type ComponentTypes from "../../componentTypes";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { CMSTeaserContainer } from "~/CMSComponents/components/CMSTeaserContainer/CMSTeaserContainer";
import { Icon } from "~/common/components/icon/Icon";
import { IconSize } from "~/common/components/icon/IconSize";
import { IconType } from "~/common/components/icon/IconType";
import { type CMSFilterableTileContainerFilterDTO } from "~/CMSComponents/components/CMSFilterableTileContainer/dto";
import { useTileFilter } from "~/CMSComponents/components/CMSFilterableTileContainer/useTileFilter";
import classNames from "classnames";

export enum DesktopContainerTypes {
  DEFAULT = "DEFAULT",
  CAROUSEL = "CAROUSEL",
}

export enum MobileContainerTypes {
  DEFAULT = "DEFAULT",
  HORIZONTAL = "HORIZONTAL_SCROLL",
  CAROUSEL = "CAROUSEL",
}

export interface CMSTeasersProps {
  id: string;
  type: ComponentTypes.CMSTeaserContainer;
  teasers: CMSTeaserProps[];
  scrollType?: DesktopContainerTypes;
  itemsPerRow?: number;
  mobileScrollType?: MobileContainerTypes;
  mobileItemsPerRow?: number;
  className?: string;
  filters?: CMSFilterableTileContainerFilterDTO[];
  showAllLabel?: string;
}

export const CMSTeasers = ({
  scrollType = DesktopContainerTypes.DEFAULT,
  itemsPerRow = 1,
  mobileScrollType = MobileContainerTypes.DEFAULT,
  mobileItemsPerRow = 1,
  teasers,
  filters = [],
  className,
  showAllLabel,
}: CMSTeasersProps) => {
  const isDesktop = useIsMinDesktopSize();

  const toggle = (index: number) => setOpenIndex((prevValue) => (index === prevValue ? null : index));
  const closeContent = () => setOpenIndex(null);

  let wideness: typeof WIDE | typeof NARROW;
  if (isDesktop) {
    wideness = itemsPerRow > 2 ? NARROW : WIDE;
  } else {
    wideness = NARROW;
  }
  const renderItem = (item: CMSTeaserProps) => (
    <CMSTeaser
      {...item}
      setContent={setContent}
      onToggle={toggle}
      isExpanded={isTeaserExpanded(item.index)}
      wideness={wideness}
    />
  );

  // TODO: Temporary code, need to assign index, wrong type is coming above
  const tiles: CMSTeaserProps[] = teasers.map((teaser, index) => ({ ...teaser, index }));

  const { filterContainer, visibleTiles } = useTileFilter({ filters, showAllLabel, allTiles: tiles });

  const [content, setContent] = useState<CMSSectionComponentProps | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isTeaserExpanded = (index: number) => index === openIndex;

  const renderTeaserDetailsContainer = (validIds: number[]) => {
    if (openIndex === null || !validIds.includes(openIndex)) {
      return null;
    }

    const className = content?.fullWidth === true ? "scha__teasers__target__full" : "scha__teasers__target";

    return (
      <div className={"scha__teasers__container scha__teasers__container--active"}>
        <div className={"scha__input-with-x scha__teasers__x"} onClick={closeContent}>
          <Icon size={IconSize.FONT_24} type={IconType.Close} />
        </div>
        {content && (
          <div className={className}>
            <CMSSection {...content} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={classNames("scha__cms__filterable-container", className)}>
      {filterContainer}

      <CMSTeaserContainer
        scrollType={scrollType}
        itemsPerRow={itemsPerRow}
        mobileScrollType={mobileScrollType}
        mobileItemsPerRow={mobileItemsPerRow}
        className={className}
        teasers={visibleTiles}
        renderTeaser={renderItem}
        renderTeaserDetailsContainer={renderTeaserDetailsContainer}
      />
    </div>
  );
};
